import clsx from "clsx";
import type { LinkProps } from "react-router";
import { Link as BaseLink } from "react-router";
import styles from "./Link.module.scss";
import type { IText } from "../Text/Text";
import { MICROSOFT_TEAMS_PHONE_CALL_PREFIX } from "@medoma/models/phoneNumbers";

export type ILink = LinkProps & {
  to: string;
  type?:
    | "internal"
    | "external"
    | "phone"
    | "email"
    | "microsoft-teams-phone-call";
  color?: "primary" | "neutral";
} & Pick<IText, "size" | "weight">;

export const Link = ({
  children,
  className,
  type = "internal",
  to,
  size,
  weight = "medium",
  color,
  ...rest
}: ILink) => {
  const classNames = clsx(
    styles.link,
    styles[`size-${size}`],
    styles[`weight-${weight}`],
    styles[`color-${color}`],
    className,
  );

  return type === "external" ? (
    <a
      className={classNames}
      href={to}
      target="_blank"
      rel="noreferrer"
      {...rest}
    >
      {children}
    </a>
  ) : type === "phone" ? (
    <a className={classNames} href={`tel:${to}`} {...rest}>
      {children}
    </a>
  ) : type === "microsoft-teams-phone-call" ? (
    <a
      className={classNames}
      href={`${MICROSOFT_TEAMS_PHONE_CALL_PREFIX}${to}`}
      {...rest}
    >
      {children}
    </a>
  ) : type === "email" ? (
    <a className={classNames} href={`mailto:${to}`} {...rest}>
      {children}
    </a>
  ) : (
    <BaseLink className={classNames} to={to} {...rest}>
      {children}
    </BaseLink>
  );
};

export type IUnstyledLink = LinkProps & {
  type?: "internal" | "external" | "phone" | "email";
  to: string;
};

export const UnstyledLink = ({
  children,
  type = "internal",
  to,
  ...rest
}: IUnstyledLink) => {
  return type === "external" ? (
    <a
      href={to}
      className={styles.unstyledLink}
      target="_blank"
      rel="noreferrer"
      {...rest}
    >
      {children}
    </a>
  ) : type === "phone" ? (
    <a href={`tel:${to}`} className={styles.unstyledLink} {...rest}>
      {children}
    </a>
  ) : type === "email" ? (
    <a href={`mailto:${to}`} className={styles.unstyledLink} {...rest}>
      {children}
    </a>
  ) : (
    <BaseLink to={to} className={styles.unstyledLink} {...rest}>
      {children}
    </BaseLink>
  );
};
